import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import DateRangePicker from '@components/common/DateRangePicker';
import SearchInput from '@components/common/SearchInput';
import { api } from '@/api/growth/season/api';
import {
    SeasonListItem,
    SeasonListRes,
    SeasonListReq,
} from '@/api/growth/season/type';
import DataTable, {
    ColumnConfig,
    HeaderButton,
} from '@components/common/DataTable/DataTable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    objectToQueryString,
    queryStringToObject,
    typeConverters,
} from '@utils/Utils';
import { CorporationDefaultInfo } from '@/api/corporation/type';
import ActionButton from '@components/common/ActionButton/ActionButton';
import { useAuthStore } from '@/store/authStore';

interface SeasonListProps {
    userIdx?: number;
    userName?: string;
}

const SeasonList = ({
    userIdx: propUserIdx,
    userName: propUserName,
}: SeasonListProps) => {
    const { idx: authUserIdx, name: authName } = useAuthStore();

    // userMode(role === 1)일 경우 authStore 값을 fallback으로 사용
    const userIdx = propUserIdx ?? authUserIdx ?? 0;
    const userName = propUserName ?? authName ?? '';

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [filters, setFilters] = useState<SeasonListReq>(() => {
        const urlFilters = queryStringToObject<SeasonListReq>(searchParams, {
            nowPage: typeConverters.number,
            pageSize: typeConverters.number,
        });

        return {
            userIdx: userIdx,
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
            startDt: undefined,
            endDt: undefined,
            ...urlFilters,
        };
    });

    const updateFilter = (key: keyof SeasonListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setFilters(prev => ({
            ...prev,
            startDt: start ? format(start, 'yyyy-MM-dd') : undefined,
            endDt: end ? format(end, 'yyyy-MM-dd') : undefined,
            nowPage: 1,
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [seasonRes, setSeasonListRes] = useState<SeasonListRes>();

    const fetchSeasonList = useCallback(async () => {
        const response = await api.seasonList(filters);
        setSeasonListRes(response);
    }, [filters]);

    const getCurrentFilterParams = (): string => {
        return objectToQueryString(filters, {
            defaultValues: {
                pageSize: 10,
                nowPage: 1,
                pageGroup: 10,
                keyword: '',
            },
        });
    };

    const onClickRow = (row: SeasonListItem) => {
        const currentParams = getCurrentFilterParams();
        navigate(
            `/season/${row.gsIdx}?returnUrl=${encodeURIComponent(currentParams)}`,
        );
    };

    const VALID_STATUS_MAP = {
        true: { text: '진행중', className: 'bg-success' },
        false: { text: '종료됨', className: 'bg-danger' },
    } as const;

    const columns: ColumnConfig<SeasonListItem>[] = [
        { key: 'gsIdx', header: 'No' },
        { key: 'name', header: '작기명', clickable: true },
        { key: 'startDt', header: '작기 시작' },
        { key: 'endDt', header: '작기 종료' },
        {
            key: 'farmlandCnt',
            header: '농지수',
            render: value => {
                return `${value.toLocaleString()}`;
            },
        },
        {
            key: 'cultivationArea',
            header: '면적',
            render: value => {
                return `${value.toLocaleString()}㎡`;
            },
        },
        {
            key: 'isValid',
            header: '진행여부',
            render: value => {
                const statusInfo =
                    VALID_STATUS_MAP[value as keyof typeof VALID_STATUS_MAP];
                return (
                    <span className={`badge ${statusInfo.className}`}>
                        {statusInfo.text}
                    </span>
                );
            },
        },
        { key: 'regDt', header: '등록일자' },
        { key: 'updDt', header: '갱신일자', render: value => value || '-' },
        {
            key: 'actions',
            header: '선택',
            clickable: false,
            render: (_value, row) => (
                <ActionButton<SeasonListItem>
                    type="save"
                    size="sm"
                    text="농지관리"
                    className="btn-primary-light"
                    icon="ri-pin-distance-fill"
                    onClick={() => handleBtnClick(row)}
                />
            ),
        },
    ];

    const headerButtons: HeaderButton[] = [
        {
            label: '작기 등록',
            icon: 'ri-add-line',
            className: 'btn btn-sm btn-primary btn-wave',
            onClick: () => {
                const corporationInfo: CorporationDefaultInfo = {
                    userIdx,
                    userName,
                };
                navigate(`/season/add`, {
                    state: corporationInfo,
                });
            },
        },
    ];

    const handleBtnClick = (row: SeasonListItem) => {
        navigate(`/farmland/${row.gsIdx}`);
    };

    useEffect(() => {
        fetchSeasonList();
    }, [fetchSeasonList]);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-3">
                                    <DateRangePicker
                                        startDate={
                                            filters.startDt
                                                ? new Date(filters.startDt)
                                                : null
                                        }
                                        endDate={
                                            filters.endDt
                                                ? new Date(filters.endDt)
                                                : null
                                        }
                                        onChange={handleDateChange}
                                        placeholder="날짜 범위를 선택하세요"
                                    />
                                </div>
                                <div className="col-12 col-md-9">
                                    <SearchInput
                                        placeholder="작기명을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    {seasonRes && (
                        <DataTable<SeasonListItem>
                            data={seasonRes?.data}
                            columns={columns}
                            headerButtons={headerButtons}
                            paging={seasonRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onClickRow={onClickRow}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default SeasonList;
